import { useState } from 'react';
import { TablePager, useSeiten } from '../components/TablePager';
import { FormError } from '../components/FormError';
import { EuroInput } from '../components/EuroInput';
import { EditIconButton, RowActionGroup } from '../components/RowActions';
import { keepPreviousData, useQueryClient } from '@tanstack/react-query';
import { useDebouncedValue } from '@mantine/hooks';
import {
  Badge,
  Button,
  Group,
  Modal,
  Select,
  Stack,
  Switch,
  Table,
  TextInput,
} from '@mantine/core';
import {
  ApiError,
  masterDataDeleteControllerArticle,
  useArticlesControllerList,
  useArticlesControllerCreate,
  useArticlesControllerUpdate,
  getArticlesControllerListQueryKey,
  type ArticleResponseDtoOutput,
} from '@werkbank/shared';
import { useGemerkt } from '../lib/gemerkt';
// Vollanalyse S1: Positionsart- und Steuersatz-Labels aus der kanonischen
// Quelle (lib/format → @werkbank/shared) statt lokaler Kopien.
import { ITEM_KIND_LABELS, TAX_CODE_LABELS, formatCents, selectOptions } from '../lib/format';
import { ListPage } from '../components/ListPage';
import { listState } from '../components/listPageContract';
import { listMeta } from '../components/listPageContract';
import { TableCard } from '../components/TableCard';
import { EmptyState } from '../components/EmptyState';
import { DeleteButton } from '../components/MasterDataDialogs';
import { useHasFlag } from '../lib/permissions';
import { EditDialogFooter } from '../components/MasterDataDialogs';

// P5-A §4: Artikeltyp Material/Dienstleistung — additive Enum-Ergänzung neben
// dem bestehenden itemKind (LABOR/MATERIAL/…). Grundlage für die §35a-
// Lohnanteil-Definition (Std.-Positionen = SERVICE). Eigenes Enum, bewusst
// KEINE Teilmenge von ITEM_KIND_LABELS.
const ARTICLE_KIND_LABELS: Record<string, string> = {
  MATERIAL: 'Material',
  SERVICE: 'Dienstleistung',
};

// Vollanalyse S2: das lokale, namensgleiche formatCents (Intl-Weg, rundet)
// ist gelöscht — es gilt der kanonische Helfer aus lib/format (delegiert an
// @werkbank/calc.formatAmountDe, trunkiert; Preise sind ganze Cents, die
// Anzeige bleibt identisch).

interface ArticleFormValues {
  sku: string;
  name: string;
  description: string;
  unit: string;
  itemKind: 'LABOR' | 'MATERIAL' | 'TRAVEL' | 'EQUIPMENT' | 'OTHER';
  kind: 'MATERIAL' | 'SERVICE';
  defaultTaxCode: 'DE_19' | 'DE_7' | 'RC_13B';
  priceEuro: number | string;
  positionTextTemplateId: string | null;
}

const EMPTY: ArticleFormValues = {
  sku: '',
  name: '',
  description: '',
  unit: 'Stk',
  itemKind: 'MATERIAL',
  kind: 'MATERIAL',
  defaultTaxCode: 'DE_19',
  priceEuro: 0,
  positionTextTemplateId: null,
};

export function ArticlesPage() {
  // Tobias 2026-09-25: Filter bleiben beim Wechsel der Seite erhalten.
  const [search, setSearch] = useGemerkt('artikel.suche', '');
  const [showArchived, setShowArchived] = useGemerkt('artikel.archiv', false);
  // P5-A §4: Filter nach Artikeltyp (Alle/Material/Dienstleistung).
  const [kindFilter, setKindFilter] = useGemerkt<'ALL' | 'MATERIAL' | 'SERVICE'>(
    'artikel.typ',
    'ALL',
  );
  const [editing, setEditing] = useState<ArticleResponseDtoOutput | 'new' | null>(null);
  const [values, setValues] = useState<ArticleFormValues>(EMPTY);
  const queryClient = useQueryClient();
  // Kundentest 2026-08: Löschen je Zeile — hartes Löschen ist die einzige
  // irreversible Stammdatenaktion (4.1b), daher eigener Flag deleteMasterData.
  const canDelete = useHasFlag('deleteMasterData');

  // Vollanalyse M19: die Suche läuft serverseitig (die API kappt bei 200) —
  // debounced, damit nicht jeder Tastendruck eine Anfrage auslöst;
  // placeholderData hält die letzte Liste sichtbar (kein Flackern beim Tippen).
  const [debouncedSearch] = useDebouncedValue(search.trim(), 250);
  const articles = useArticlesControllerList(
    {
      search: debouncedSearch || undefined,
      archived: showArchived ? 'all' : 'false',
      take: 1000,
      kind: kindFilter === 'ALL' ? undefined : kindFilter,
    },
    { query: { placeholderData: keepPreviousData } },
  );

  const done = () => {
    void queryClient.invalidateQueries({ queryKey: getArticlesControllerListQueryKey() });
    setEditing(null);
  };
  const create = useArticlesControllerCreate<ApiError>({ mutation: { onSuccess: done } });
  const update = useArticlesControllerUpdate<ApiError>({ mutation: { onSuccess: done } });

  const openNew = () => {
    setValues(EMPTY);
    setEditing('new');
  };
  const openEdit = (a: ArticleResponseDtoOutput) => {
    setValues({
      sku: a.sku,
      name: a.name,
      description: a.description ?? '',
      unit: a.unit,
      itemKind: a.itemKind,
      kind: a.kind,
      defaultTaxCode: a.defaultTaxCode,
      priceEuro: a.listPriceCents / 100,
      positionTextTemplateId: a.positionTextTemplateId ?? null,
    });
    setEditing(a);
  };

  const save = () => {
    const listPriceCents = Math.round(Number(values.priceEuro || 0) * 100);
    const payload = {
      sku: values.sku.trim(),
      name: values.name.trim(),
      description: values.description.trim() || undefined,
      unit: values.unit.trim(),
      itemKind: values.itemKind,
      kind: values.kind,
      defaultTaxCode: values.defaultTaxCode,
      listPriceCents,
      positionTextTemplateId: values.positionTextTemplateId,
    };
    if (editing === 'new') create.mutate({ data: payload });
    else if (editing) update.mutate({ id: editing.id, data: payload });
  };

  const error = create.error ?? update.error;

  // D-W1 QS-3: der Zähler steht nur noch im Kartenfuß; die Kopf-Meta trägt
  // Beschreibung und aktive Filter (Vollanalyse S6: gemeinsamer listMeta-Helfer).
  const meta = listMeta(
    !!articles.data,
    'Artikelstamm für Positionen und Belege',
    showArchived ? 'inkl. archivierte' : null,
    search ? `Suche „${search}“` : null,
  );

  // D-W1 Welle 2 (A2-13, W2-B4): Seite auf das ListPage-Gerüst migriert —
  // die vier Zustände (Laden/Fehler/Leer/Gefüllt) trägt das Gerüst.
  const seiten = useSeiten(articles.data, 'artikel');
  return (
    <>
      <ListPage
        title="Artikel"
        meta={meta}
        action={
          <Button onClick={openNew} data-testid="article-create">
            Neuer Artikel
          </Button>
        }
        filters={
          <>
            <TextInput
              placeholder="Suche (Nummer, Name)…"
              value={search}
              onChange={(e) => setSearch(e.currentTarget.value)}
              w={280}
              data-testid="article-search"
            />
            <Select
              aria-label="Artikeltyp"
              w={180}
              allowDeselect={false}
              data={[
                { value: 'ALL', label: 'Alle Typen' },
                { value: 'MATERIAL', label: 'Material' },
                { value: 'SERVICE', label: 'Dienstleistung' },
              ]}
              value={kindFilter}
              onChange={(v) => v && setKindFilter(v as 'ALL' | 'MATERIAL' | 'SERVICE')}
              data-testid="article-kind-filter"
            />
            <Switch
              label="Archivierte anzeigen"
              checked={showArchived}
              onChange={(e) => setShowArchived(e.currentTarget.checked)}
            />
          </>
        }
        state={listState(articles, articles.data?.length === 0)}
        errorText="Artikel konnten nicht geladen werden."
        empty={
          <TableCard testId="articles-card">
            <EmptyState
              text={
                search || showArchived
                  ? 'Keine Artikel für diese Filter gefunden.'
                  : 'Noch keine Artikel angelegt.'
              }
              action={<Button onClick={openNew}>Neuer Artikel</Button>}
            />
          </TableCard>
        }
      >
        <TableCard
          testId="articles-card"
          // Vollanalyse M19: exakt 200 Zeilen = serverseitige Kappung erreicht —
          // Hinweis statt nackter Zählung, damit niemand die Liste für vollständig hält.
          footer={
            <TablePager p={seiten} einzahl="Artikel" mehrzahl="Artikel" testId="articles-pager" />
          }
        >
          <Table highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Artikelnr.</Table.Th>
                <Table.Th>Bezeichnung</Table.Th>
                <Table.Th>Art</Table.Th>
                <Table.Th>Einheit</Table.Th>
                <Table.Th>USt</Table.Th>
                <Table.Th style={{ textAlign: 'right' }}>Listenpreis</Table.Th>
                <Table.Th aria-label="Aktionen" />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {seiten.sichtbar.map((a) => (
                <Table.Tr
                  key={a.id}
                  style={{ cursor: 'pointer', opacity: a.archived ? 0.5 : 1 }}
                  onClick={() => openEdit(a)}
                >
                  <Table.Td>{a.sku}</Table.Td>
                  <Table.Td>
                    <Group gap="xs">
                      {a.name}
                      {/* P5-A §4: Artikeltyp als leiser Chip. */}
                      <Badge
                        variant="light"
                        color="gray"
                        size="xs"
                        data-testid={`article-kind-${a.id}`}
                      >
                        {ARTICLE_KIND_LABELS[a.kind] ?? a.kind}
                      </Badge>
                      {a.archived && (
                        <Badge color="gray" size="xs">
                          archiviert
                        </Badge>
                      )}
                    </Group>
                  </Table.Td>
                  <Table.Td>{ITEM_KIND_LABELS[a.itemKind]}</Table.Td>
                  <Table.Td>{a.unit}</Table.Td>
                  <Table.Td>{TAX_CODE_LABELS[a.defaultTaxCode]}</Table.Td>
                  <Table.Td style={{ textAlign: 'right' }}>
                    {formatCents(a.listPriceCents)} €
                  </Table.Td>
                  {/* Zeilenaktionen (Faruq 2026-08-24): der Stift ist der
                      sichtbare Weg in die Bearbeitung — der Zeilenklick bleibt
                      als Abkuerzung, war aber nirgends angeschrieben. Loeschen
                      nur mit dem Recht: hart und nur unreferenziert, sonst zeigt
                      der Knopf die feldgenaue Serverantwort mit dem Hinweis,
                      stattdessen zu archivieren (4.1b). stopPropagation, damit
                      der Zeilenklick nicht zusaetzlich dazwischenfunkt. */}
                  <Table.Td onClick={(e) => e.stopPropagation()} style={{ cursor: 'default' }}>
                    <RowActionGroup>
                      <EditIconButton
                        title={`Artikel „${a.name}" bearbeiten`}
                        onClick={() => openEdit(a)}
                        testId={`article-edit-${a.id}`}
                      />
                      {canDelete && (
                        <DeleteButton
                          asIcon
                          label="Löschen"
                          title={`Artikel „${a.name}" löschen`}
                          question={`Soll der Artikel „${a.name}" gelöscht werden?`}
                          detail="Möglich nur, solange er nirgends verwendet wird. Sonst nennt die Meldung, wo er noch hängt — dann ist Archivieren der Weg."
                          onDelete={() =>
                            masterDataDeleteControllerArticle(a.id).then(() =>
                              queryClient.invalidateQueries({
                                queryKey: getArticlesControllerListQueryKey(),
                              }),
                            )
                          }
                          testId={`article-delete-${a.id}`}
                        />
                      )}
                    </RowActionGroup>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </TableCard>
      </ListPage>

      <Modal
        opened={editing !== null}
        onClose={() => setEditing(null)}
        title={editing === 'new' ? 'Neuer Artikel' : 'Artikel bearbeiten'}
      >
        <Stack gap="md">
          <Group grow>
            <TextInput
              label="Artikelnummer"
              required
              disabled={editing !== 'new'}
              value={values.sku}
              onChange={(e) => setValues({ ...values, sku: e.currentTarget.value })}
              data-testid="article-sku"
            />
            <TextInput
              label="Einheit"
              required
              value={values.unit}
              onChange={(e) => setValues({ ...values, unit: e.currentTarget.value })}
            />
          </Group>
          <TextInput
            label="Bezeichnung"
            required
            value={values.name}
            onChange={(e) => setValues({ ...values, name: e.currentTarget.value })}
            data-testid="article-name"
          />
          <TextInput
            label="Beschreibung (optional)"
            value={values.description}
            onChange={(e) => setValues({ ...values, description: e.currentTarget.value })}
          />
          <Group grow>
            <Select
              label="Art"
              data={selectOptions(ITEM_KIND_LABELS)}
              allowDeselect={false}
              value={values.itemKind}
              onChange={(v) =>
                v && setValues({ ...values, itemKind: v as ArticleFormValues['itemKind'] })
              }
            />
            {/* P5-A §4: Typ (Material/Dienstleistung) — eigenes Feld neben „Art“. */}
            <Select
              label="Typ"
              data={selectOptions(ARTICLE_KIND_LABELS)}
              allowDeselect={false}
              value={values.kind}
              onChange={(v) => v && setValues({ ...values, kind: v as ArticleFormValues['kind'] })}
              data-testid="article-kind"
            />
            <Select
              label="Steuersatz"
              data={selectOptions(TAX_CODE_LABELS)}
              allowDeselect={false}
              value={values.defaultTaxCode}
              onChange={(v) =>
                v &&
                setValues({ ...values, defaultTaxCode: v as ArticleFormValues['defaultTaxCode'] })
              }
            />
          </Group>
          <EuroInput
            label="Listenpreis (EUR, netto)"
            fixedDecimalScale
            value={values.priceEuro}
            onChange={(v) => setValues({ ...values, priceEuro: v })}
            data-testid="article-price"
          />
          {editing !== 'new' && editing && (
            <Switch
              label="Archiviert"
              checked={editing.archived}
              onChange={(e) =>
                update.mutate({ id: editing.id, data: { archived: e.currentTarget.checked } })
              }
            />
          )}
          <FormError error={error} />
          <EditDialogFooter
            error={null}
            onCancel={() => setEditing(null)}
            onSave={save}
            saving={create.isPending || update.isPending}
            saveTestId="article-save"
          />
        </Stack>
      </Modal>
    </>
  );
}
